/**
 * WebCrypto utilities for end-to-end encryption using EC P-384
 *
 * Algorithm:
 *   - ECDH (P-384) key agreement → AES-256-GCM encryption (12-byte IV, 128-bit tag)
 *   - ECDSA (P-384, SHA-384) signing of the ciphertext
 *
 * Wire format: Base64(IV):Base64(ciphertext):Base64(signature)
 *
 * Matches spacelinq-android BouncyCastleE2EEncryption:
 *   ECCDH → AES[256]/GCM/NoPadding, IV_LENGTH=12, TAG=128, SHA384withECDSA
 *
 * Signature encoding: Web Crypto produces IEEE P1363 (raw R||S, 96 bytes for P-384).
 * Android's BouncyCastle FIPS SHA384withECDSA expects DER/ASN.1 format (~103 bytes).
 * p1363ToDer converts before sending; derToP1363 converts received Android signatures
 * before verifying with Web Crypto.
 */

const CURVE = "P-384";
const DELIMITER = ":";
const IV_LENGTH = 12;

export interface KeyPairInfo {
  keyId: string;
  encryptPublicKey: CryptoKey;
  encryptPrivateKey: CryptoKey;
  signPublicKey: CryptoKey;
  signPrivateKey: CryptoKey;
  encryptPublicKeyJwk: JsonWebKey;
  signPublicKeyJwk: JsonWebKey;
  createdAt: number;
}

// Legacy RSA wire format — kept only so old stored messages can be detected and
// gracefully shown as unreadable after the key migration.
export interface LegacyEncryptedMessage {
  ciphertext: string;
  keyId: string;
  algorithm: string;
  senderCiphertext?: string;
  senderKeyId?: string;
}

// Re-exported under the old name so callers don't need immediate updates.
export type EncryptedMessage = LegacyEncryptedMessage;

// ---------------------------------------------------------------------------
// Key generation
// ---------------------------------------------------------------------------

export async function generateKeyPair(): Promise<KeyPairInfo> {
  const [encryptPair, signPair] = await Promise.all([
    crypto.subtle.generateKey(
      { name: "ECDH", namedCurve: CURVE },
      true,
      ["deriveKey", "deriveBits"]
    ),
    crypto.subtle.generateKey(
      { name: "ECDSA", namedCurve: CURVE },
      true,
      ["sign", "verify"]
    ),
  ]);

  const [encryptPublicKeyJwk, signPublicKeyJwk] = await Promise.all([
    crypto.subtle.exportKey("jwk", encryptPair.publicKey),
    crypto.subtle.exportKey("jwk", signPair.publicKey),
  ]);

  const keyId = await generateKeyId(encryptPublicKeyJwk);

  return {
    keyId,
    encryptPublicKey: encryptPair.publicKey,
    encryptPrivateKey: encryptPair.privateKey,
    signPublicKey: signPair.publicKey,
    signPrivateKey: signPair.privateKey,
    encryptPublicKeyJwk,
    signPublicKeyJwk,
    createdAt: Date.now(),
  };
}

async function generateKeyId(jwk: JsonWebKey): Promise<string> {
  const data = new TextEncoder().encode(JSON.stringify(jwk));
  const hash = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("")
    .slice(0, 16);
}

// ---------------------------------------------------------------------------
// Encrypt / decrypt (ECDH + AES-GCM + ECDSA)
// ---------------------------------------------------------------------------

async function deriveSharedAesKey(
  ownEncPrivateKey: CryptoKey,
  peerEncPublicKey: CryptoKey
): Promise<CryptoKey> {
  return crypto.subtle.deriveKey(
    { name: "ECDH", public: peerEncPublicKey },
    ownEncPrivateKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

/**
 * Encrypt `message` for a recipient and sign with the sender's sign key.
 * Returns the colon-delimited wire string: Base64(IV):Base64(ciphertext):Base64(signature)
 */
export async function encryptMessage(
  message: string,
  recipientEncPublicKeyJwk: JsonWebKey,
  senderEncPrivateKeyJwk: JsonWebKey,
  senderSignPrivateKeyJwk: JsonWebKey
): Promise<string> {
  const [recipientEncPublicKey, senderEncPrivateKey, senderSignPrivateKey] =
    await Promise.all([
      importEncryptPublicKey(recipientEncPublicKeyJwk),
      importEncryptPrivateKey(senderEncPrivateKeyJwk),
      importSignPrivateKey(senderSignPrivateKeyJwk),
    ]);

  const aesKey = await deriveSharedAesKey(senderEncPrivateKey, recipientEncPublicKey);

  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv, tagLength: 128 },
    aesKey,
    new TextEncoder().encode(message)
  );

  const rawSig = await crypto.subtle.sign(
    { name: "ECDSA", hash: "SHA-384" },
    senderSignPrivateKey,
    ciphertext
  );
  // Convert P1363 (Web Crypto) → DER (BouncyCastle FIPS expects DER for SHA384withECDSA)
  const signature = p1363ToDer(rawSig);

  return [
    arrayBufferToBase64(iv.buffer),
    arrayBufferToBase64(ciphertext),
    arrayBufferToBase64(signature),
  ].join(DELIMITER);
}

/**
 * Decrypt a colon-delimited ECDH wire message.
 * Both sender and recipient derive the same AES key via ECDH — no second ciphertext needed.
 */
export async function decryptMessage(
  wireMessage: string,
  ownEncPrivateKeyJwk: JsonWebKey,
  peerEncPublicKeyJwk: JsonWebKey
): Promise<string> {
  const parts = wireMessage.split(DELIMITER);
  if (parts.length !== 3) throw new Error("Invalid ECDH wire format");

  const [ownEncPrivateKey, peerEncPublicKey] = await Promise.all([
    importEncryptPrivateKey(ownEncPrivateKeyJwk),
    importEncryptPublicKey(peerEncPublicKeyJwk),
  ]);

  const aesKey = await deriveSharedAesKey(ownEncPrivateKey, peerEncPublicKey);

  const decrypted = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: base64ToArrayBuffer(parts[0]), tagLength: 128 },
    aesKey,
    base64ToArrayBuffer(parts[1])
  );

  return new TextDecoder().decode(decrypted);
}

// ---------------------------------------------------------------------------
// Group message crypto (symmetric AES-256-GCM + ECDSA)
// Matches Android's encryptWithSymmetricKey / decryptGroupMessageWithSymmetricKey
// ---------------------------------------------------------------------------

/**
 * Generates a random AES-256 symmetric key and returns it as a Base64 string.
 * Used by the group owner to create a per-group encryption key.
 */
export async function generateSymmetricKey(): Promise<string> {
  const key = await crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt", "decrypt"]
  );
  const raw = await crypto.subtle.exportKey("raw", key);
  return arrayBufferToBase64(raw);
}

/**
 * Encrypts a group message with the group's symmetric key and signs with the
 * sender's ECDSA private key.
 *
 * Wire format: Base64(IV):Base64(ciphertext):Base64(signature)
 * Matches Android's standard 3-part encryptWithSymmetricKey format.
 * Sender identity is carried by message routing metadata (rawAddress.mid), not the cipher.
 */
export async function encryptGroupMessage(
  message: string,
  symmetricKeyB64: string,
  signPrivKeyJwk: JsonWebKey
): Promise<string> {
  const aesKey = await crypto.subtle.importKey(
    "raw",
    base64ToArrayBuffer(symmetricKeyB64),
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt"]
  );

  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv, tagLength: 128 },
    aesKey,
    new TextEncoder().encode(message)
  );

  const signPrivKey = await importSignPrivateKey(signPrivKeyJwk);
  const rawSig = await crypto.subtle.sign(
    { name: "ECDSA", hash: "SHA-384" },
    signPrivKey,
    ciphertext
  );
  // Convert P1363 → DER so Android's BouncyCastle SHA384withECDSA can verify it
  const signature = p1363ToDer(rawSig);

  return [
    arrayBufferToBase64(iv.buffer),
    arrayBufferToBase64(ciphertext),
    arrayBufferToBase64(signature),
  ].join(DELIMITER);
}

/**
 * Decrypts a group message wire string.
 *
 * Current format:  IV:ciphertext:signature  → senderUsername is null (from routing metadata)
 * Legacy format:   senderUsername:IV:ciphertext:signature  → senderUsername is returned
 *
 * Note: signature verification is separate (authenticateGroupMessage).
 */
export async function decryptGroupMessage(
  wireMessage: string,
  symmetricKeyB64: string
): Promise<{ plaintext: string; senderUsername: string | null }> {
  const parts = wireMessage.split(DELIMITER);

  let senderUsername: string | null;
  let ivB64: string;
  let ciphertextB64: string;

  if (parts.length === 4) {
    [senderUsername, ivB64, ciphertextB64] = parts;
  } else if (parts.length === 3) {
    senderUsername = null;
    [ivB64, ciphertextB64] = parts;
  } else {
    throw new Error("Invalid group message wire format (expected 3 or 4 parts)");
  }

  const aesKey = await crypto.subtle.importKey(
    "raw",
    base64ToArrayBuffer(symmetricKeyB64),
    { name: "AES-GCM", length: 256 },
    false,
    ["decrypt"]
  );

  const plaintext = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: base64ToArrayBuffer(ivB64), tagLength: 128 },
    aesKey,
    base64ToArrayBuffer(ciphertextB64)
  );

  return { plaintext: new TextDecoder().decode(plaintext), senderUsername };
}

/**
 * Verifies the ECDSA signature on a group message wire string.
 * Handles both DER (Android/BouncyCastle) and P1363 (WebCrypto) encoded signatures:
 * P-384 P1363 is exactly 96 bytes; anything larger is treated as DER and converted.
 *
 * Accepts both wire formats (matching Android's EncryptedMessageComponents):
 *   Current:  IV:ciphertext:signature
 *   Legacy:   senderUsername:IV:ciphertext:signature
 */
export async function authenticateGroupMessage(
  wireMessage: string,
  senderSignPubKeyJwk: JsonWebKey
): Promise<boolean> {
  const parts = wireMessage.split(DELIMITER);

  let ciphertextB64: string;
  let sigB64: string;

  if (parts.length === 4) {
    ciphertextB64 = parts[2];
    sigB64 = parts[3];
  } else if (parts.length === 3) {
    ciphertextB64 = parts[1];
    sigB64 = parts[2];
  } else {
    return false;
  }

  try {
    const signPubKey = await importSignPublicKey(senderSignPubKeyJwk);
    const sigBuf = base64ToArrayBuffer(sigB64);
    // SubtleCrypto verify expects P1363; convert DER (>96 bytes) from Android if needed
    const sigForVerify = new Uint8Array(sigBuf).length === 96 ? sigBuf : derToP1363(sigBuf);
    return await crypto.subtle.verify(
      { name: "ECDSA", hash: "SHA-384" },
      signPubKey,
      sigForVerify,
      base64ToArrayBuffer(ciphertextB64)
    );
  } catch {
    return false;
  }
}

/**
 * Detects group message wire formats.
 *
 * Current format: Base64(IV):Base64(ciphertext):Base64(sig)
 * Legacy format:  senderUsername:Base64(IV):Base64(ciphertext):Base64(sig)
 *
 * The IV is exactly 16 base64 chars (12 bytes). The signature is ≥128 chars.
 * 3-part is only distinguishable from a 1:1 ECDH message by conversation context
 * (use isGroupMessageWireFormat(msg, isGroupConversation) for 3-part detection).
 */
export function isGroupMessageWireFormat(message: string, groupContext = false): boolean {
  const parts = message.split(DELIMITER);
  if (parts.length === 4) {
    return (
      parts[1].length === 16 && isBase64Like(parts[1]) &&
      parts[2].length >= 24 && isBase64Like(parts[2]) &&
      parts[3].length >= 128 && isBase64Like(parts[3])
    );
  }
  if (parts.length === 3 && groupContext) {
    return (
      parts[0].length === 16 && isBase64Like(parts[0]) &&
      parts[1].length >= 24 && isBase64Like(parts[1]) &&
      parts[2].length >= 128 && isBase64Like(parts[2])
    );
  }
  return false;
}

// ---------------------------------------------------------------------------
// Legacy RSA path — only for messages encrypted before the EC migration
// ---------------------------------------------------------------------------

export async function decryptLegacyMessage(
  encryptedMsg: LegacyEncryptedMessage,
  privateKey: CryptoKey
): Promise<string> {
  const decoder = new TextDecoder();
  try {
    const buf = await crypto.subtle.decrypt(
      { name: "RSA-OAEP" },
      privateKey,
      base64ToArrayBuffer(encryptedMsg.ciphertext)
    );
    return decoder.decode(buf);
  } catch {
    if (encryptedMsg.senderCiphertext) {
      const buf = await crypto.subtle.decrypt(
        { name: "RSA-OAEP" },
        privateKey,
        base64ToArrayBuffer(encryptedMsg.senderCiphertext)
      );
      return decoder.decode(buf);
    }
    throw new Error("Legacy RSA decryption failed");
  }
}

// ---------------------------------------------------------------------------
// Format detection
// ---------------------------------------------------------------------------

/**
 * Returns true for both the new ECDH wire format and the legacy RSA JSON format.
 */
export function isEncryptedMessage(message: string): boolean {
  if (!message) return false;
  if (isEcdhWireFormat(message)) return true;
  try {
    const p = JSON.parse(message);
    return !!(p.ciphertext && p.keyId && p.algorithm);
  } catch {
    return false;
  }
}

/**
 * Detects the new ECDH wire format: Base64(IV):Base64(ciphertext):Base64(signature).
 * Length guards make accidental false positives essentially impossible.
 */
export function isEcdhWireFormat(message: string): boolean {
  const parts = message.split(DELIMITER);
  if (parts.length !== 3) return false;
  const [iv, ciphertext, signature] = parts;
  return (
    iv.length >= 16 && isBase64Like(iv) &&       // IV: 12 bytes → 16 b64 chars
    ciphertext.length >= 24 && isBase64Like(ciphertext) && // min 1-byte msg + 16-byte tag
    signature.length >= 80 && isBase64Like(signature)      // P-384 sig: 128 chars P1363 or ~140 chars DER
  );
}

function isBase64Like(s: string): boolean {
  return /^[A-Za-z0-9+/=]+$/.test(s);
}

export function parseEncryptedMessage(message: string): LegacyEncryptedMessage | null {
  try {
    const p = JSON.parse(message);
    if (p.ciphertext && p.keyId && p.algorithm) return p as LegacyEncryptedMessage;
    return null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Key import helpers
// ---------------------------------------------------------------------------

export async function importEncryptPublicKey(jwk: JsonWebKey): Promise<CryptoKey> {
  return crypto.subtle.importKey("jwk", jwk, { name: "ECDH", namedCurve: CURVE }, true, []);
}

export async function importEncryptPrivateKey(jwk: JsonWebKey): Promise<CryptoKey> {
  return crypto.subtle.importKey("jwk", jwk, { name: "ECDH", namedCurve: CURVE }, true, [
    "deriveKey",
    "deriveBits",
  ]);
}

export async function importSignPublicKey(jwk: JsonWebKey): Promise<CryptoKey> {
  return crypto.subtle.importKey("jwk", jwk, { name: "ECDSA", namedCurve: CURVE }, true, [
    "verify",
  ]);
}

export async function importSignPrivateKey(jwk: JsonWebKey): Promise<CryptoKey> {
  return crypto.subtle.importKey("jwk", jwk, { name: "ECDSA", namedCurve: CURVE }, true, [
    "sign",
  ]);
}

// Legacy RSA imports — only used for the backward-compat RSA decrypt path
export async function importPublicKey(jwk: JsonWebKey): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    "jwk",
    jwk,
    { name: "RSA-OAEP", hash: "SHA-256" },
    true,
    ["encrypt"]
  );
}

export async function importPrivateKey(jwk: JsonWebKey): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    "jwk",
    jwk,
    { name: "RSA-OAEP", hash: "SHA-256" },
    true,
    ["decrypt"]
  );
}

export async function exportPrivateKey(privateKey: CryptoKey): Promise<JsonWebKey> {
  return crypto.subtle.exportKey("jwk", privateKey);
}

export async function exportPublicKey(publicKey: CryptoKey): Promise<JsonWebKey> {
  return crypto.subtle.exportKey("jwk", publicKey);
}

// ---------------------------------------------------------------------------
// Utility
// ---------------------------------------------------------------------------

/**
 * Converts a Web Crypto IEEE P1363 signature (raw R||S) to DER/ASN.1 SEQUENCE format.
 * P-384 produces 96 bytes (48 R + 48 S); DER output is ~102–106 bytes.
 * Required because BouncyCastle FIPS SHA384withECDSA expects DER, not P1363.
 */
export function p1363ToDer(p1363: ArrayBuffer): ArrayBuffer {
  const bytes = new Uint8Array(p1363);
  const n = bytes.length / 2;

  let r = bytes.slice(0, n);
  let s = bytes.slice(n);

  // Strip leading zeros (keep at least 1 byte)
  while (r.length > 1 && r[0] === 0) r = r.slice(1);
  while (s.length > 1 && s[0] === 0) s = s.slice(1);

  // Prepend 0x00 if high bit set — DER INTEGER is signed
  if (r[0] & 0x80) r = new Uint8Array([0, ...r]);
  if (s[0] & 0x80) s = new Uint8Array([0, ...s]);

  const seqLen = 2 + r.length + 2 + s.length;
  const der = new Uint8Array(2 + seqLen);
  let off = 0;
  der[off++] = 0x30;
  der[off++] = seqLen;
  der[off++] = 0x02;
  der[off++] = r.length;
  der.set(r, off); off += r.length;
  der[off++] = 0x02;
  der[off++] = s.length;
  der.set(s, off);
  return der.buffer;
}

/**
 * Converts a DER/ASN.1 ECDSA signature to IEEE P1363 (raw R||S) format.
 * Required because Web Crypto verify() expects P1363, but Android sends DER.
 */
export function derToP1363(der: ArrayBuffer, componentLen = 48): ArrayBuffer {
  const bytes = new Uint8Array(der);
  let pos = 0;

  if (bytes[pos++] !== 0x30) throw new Error("DER: expected SEQUENCE");
  // Skip sequence length (short or long form)
  if (bytes[pos] & 0x80) pos += (bytes[pos] & 0x7f) + 1; else pos++;

  const readInt = (): Uint8Array => {
    if (bytes[pos++] !== 0x02) throw new Error("DER: expected INTEGER");
    const len = bytes[pos++];
    const val = bytes.slice(pos, pos + len);
    pos += len;
    return val;
  };

  const copyInt = (src: Uint8Array, dest: Uint8Array, offset: number) => {
    // Strip DER sign-padding zero(s) then right-align into componentLen bytes
    let start = 0;
    while (start < src.length - 1 && src[start] === 0) start++;
    const trimmed = src.slice(start);
    dest.set(trimmed, offset + componentLen - trimmed.length);
  };

  const r = readInt();
  const s = readInt();
  const result = new Uint8Array(2 * componentLen);
  copyInt(r, result, 0);
  copyInt(s, result, componentLen);
  return result.buffer;
}

export function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

export function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}
